import { Box } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { UsageEstimateDataRow } from './UsageEstimateDataRow';
import { UsageEstimateFooter } from './UsageEstimateFooter';
import { UsageEstimateHeader } from './UsageEstimateHeader';
import { UsageEstimateTitleRow } from './UsageEstimateTitleRow';
import { UsageEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { byPosition } from '@talon/web/src/utils/stableOrder';
import { EmptyState } from '@talon/web/src/components/ui/EmptyState';

export interface IUsageEstimatePanelProps {
  usageEstimate: UsageEstimate;
}

export const UsageEstimatePanel = observer(({ usageEstimate }: IUsageEstimatePanelProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [isUpdatingUsage, setIsUpdatingUsage] = useState(false);

  /** Computed **/
  const usageEstimateItems = usageEstimate.items && [...usageEstimate.items].sort(byPosition);

  /** Methods **/

  /**
   * Update this panel's UsageEstimate
   * @param usageEstimate
   * @param usageEstimateIndex
   * @returns
   */
  const updateUsageEstimate = async (usageEstimate: UsageEstimate) => {
    if (isUpdatingUsage) return;
    else if (!estimateStore.currentEstimate) {
      console.warn('No estimate loaded!');
      return;
    }

    setIsUpdatingUsage(true);
    await estimateStore.updateUsageEstimate(usageEstimate.id, usageEstimate.title);
    setIsUpdatingUsage(false);
  };

  const addEstimateItem = async () => {
    // A row priced by hand, for what the commercial's usages don't cover (a buyout, a negotiated fee)
    await estimateStore.createUsageEstimateItem({
      usageEstimateId: usageEstimate.id,
      title: 'New Usage Item',
      units: 1,
      cycles: 1,
      rate: 0,
    });
  };

  /** Render **/
  return (
    <Box
      margin="small"
      key={usageEstimate.id}
      fill="horizontal"
      round="10px"
      background="white"
      border={{ color: 'border-card' }}
      overflow="hidden"
    >
      {/* Header */}
      <UsageEstimateHeader
        usageEstimate={usageEstimate}
        setUsageEstimate={(usageEstimate) => updateUsageEstimate(usageEstimate)}
      />

      {/* Data Table */}
      <Box border={{ side: 'bottom', size: 'xsmall' }}>
        <UsageEstimateTitleRow />
        {!!usageEstimateItems?.length &&
          usageEstimateItems?.map((item, i) => (
            <UsageEstimateDataRow key={item.id} usageEstimateItem={item} index={i} useColors />
          ))}
        {!usageEstimateItems?.length && <EmptyState title="No items yet" body="Add the usage this estimate prices." />}
      </Box>

      {/* Footer */}
      <UsageEstimateFooter addEstimateItem={addEstimateItem} />
    </Box>
  );
});
